import React, { useState, useCallback, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { Database } from '@/types/supabase';

type Poet = Database['public']['Tables']['poets']['Row'];

export interface PoetSelectorProps {
  poets: Poet[];
  selectedPoet: Poet | null;
  onSelectPoet: (poet: Poet) => void;
  isLoading: boolean;
  onSearch: (query: string) => void;
  placeholder?: string;
  className?: string;
}

export function SimplePoetSelector({
  poets,
  selectedPoet,
  onSelectPoet,
  isLoading,
  onSearch,
  placeholder = "Type a poet's name",
  className
}: PoetSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    // Only add the event listener when the dropdown is open
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 10);
    }
  }, [open]);

  // Handle input changes with console logging
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("Input value changing to:", e.target.value);
    try {
      const newValue = e.target.value;
      setSearchValue(newValue);
      onSearch(newValue);
    } catch (error) {
      console.error("Error in handleInputChange:", error);
    }
  }, [onSearch]);

  // Handle poet selection
  const handleSelectPoet = useCallback((poet: Poet) => {
    console.log("Selecting poet:", poet.name);
    try {
      onSelectPoet(poet);
      setSearchValue("");
      setOpen(false);
    } catch (error) {
      console.error("Error in handleSelectPoet:", error);
    }
  }, [onSelectPoet]);

  const handleButtonClick = useCallback(() => {
    setOpen(!open);
  }, [open]);

  return (
    <div className="relative w-full" ref={containerRef}>
      {/* Button to open dropdown */}
      <Button
        variant="outline"
        className={cn("w-full text-left justify-start", className)}
        onClick={handleButtonClick}
        data-testid="poet-selector-simple"
      >
        {selectedPoet ? selectedPoet.name : placeholder}
      </Button>

      {/* Dropdown menu */}
      {open && (
        <div className="absolute w-full mt-1 bg-white border rounded-md shadow-lg z-10">
          {/* Search input */}
          <div className="p-2 border-b">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search poets..."
              className="w-full px-3 py-2 border rounded-md focus:outline-none"
              value={searchValue}
              onChange={handleInputChange}
              autoComplete="off"
            />
          </div>

          {/* Loading state */}
          {isLoading ? (
            <div className="p-4 text-center text-gray-500">
              Loading poets...
            </div>
          ) : searchValue.length < 2 ? (
            <div className="p-4 text-center text-gray-500">
              Type at least 2 characters to search
            </div>
          ) : poets.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              No poets found matching "{searchValue}"
            </div>
          ) : (
            <div className="max-h-60 overflow-auto">
              {poets.slice(0, 50).map((poet) => (
                <div
                  key={poet.id || `poet-${poet.name}`}
                  className={cn(
                    "px-4 py-2 cursor-pointer hover:bg-gray-100",
                    selectedPoet?.id === poet.id ? "bg-gray-100" : ""
                  )}
                  onClick={() => handleSelectPoet(poet)}
                >
                  <div className="flex items-center">
                    <div className="flex-1">
                      {poet.name}
                    </div>
                  </div>
                </div>
              ))}
              {poets.length > 50 && (
                <div className="px-4 py-2 text-xs text-center text-gray-500">
                  Showing 50 of {poets.length} results
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SimplePoetSelector; 